import { motion } from 'framer-motion';
import { Clock, History as HistoryIcon, Plus, Copy } from 'lucide-react';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { useTelegram } from '@/hooks/useTelegram';
import { fmtNum, mealTypeLabel } from '@/utils/format';
import type { HistoryItem, HistoryMeal } from '@/hooks/useFoodHistory';
import type { MealType } from '@/types';

// ============================================
// КАРТОЧКА ПРОДУКТА ИЗ ИСТОРИИ
// ============================================

interface HistoryItemCardProps {
  item: HistoryItem;
  onSelect?: (item: HistoryItem) => void;
}

/**
 * Карточка продукта из истории — визуально похожа на FavoriteCard.
 * Тап → onSelect (родитель открывает LogHistoryItemModal).
 */
export function HistoryItemCard({ item, onSelect }: HistoryItemCardProps) {
  const { haptic } = useTelegram();
  
  function handleClick() {
    haptic('light');
    onSelect?.(item);
  }
  
  const lastUsedShort = formatLastUsed(item.last_used);
  
  return (
    <motion.button
      type="button"
      onClick={handleClick}
      whileTap={{ scale: 0.98 }}
      className="w-full card p-3 flex items-center gap-3 text-left hover:bg-bg-elevated/40 transition-colors"
    >
      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-sky-glow/15 to-emerald-glow/10 flex items-center justify-center shrink-0">
        <HistoryIcon size={18} className="text-sky-glow" />
      </div>
      
      <div className="flex-1 min-w-0">
        <div className="font-display font-semibold text-sm text-ink truncate">
          {item.name}
        </div>
        <div className="flex items-center gap-2 text-[11px] text-ink-dim mt-0.5">
          <span>{fmtNum(item.avg_calories)} ккал</span>
          <span className="text-ink-dim/40">·</span>
          <span>{item.avg_weight_g}г</span>
        </div>
        <div className="flex items-center gap-1.5 mt-1 text-[10px] text-ink-dim/80">
          <Clock size={9} />
          <span>
            ×{item.use_count} {plural(item.use_count, 'раз', 'раза', 'раз')}
            {lastUsedShort && ` · ${lastUsedShort}`}
          </span>
        </div>
      </div>
      
      <div className="w-9 h-9 rounded-lg bg-emerald-glow/10 text-emerald-glow flex items-center justify-center shrink-0">
        <Plus size={14} />
      </div>
    </motion.button>
  );
}

// ============================================
// КАРТОЧКА ПРИЁМА ПИЩИ ИЗ ИСТОРИИ
// 🔄 ИЗМЕНЕНО: теперь открывает модалку CopyMealModal через onCopyClick
// ============================================

interface HistoryMealCardProps {
  meal: HistoryMeal;
  onCopyClick?: (meal: HistoryMeal) => void;  // 🆕 вместо onCopied
}

/**
 * Карточка приёма пищи из истории.
 * Тап «Копировать» — открывает модалку выбора куда копировать.
 */
export function HistoryMealCard({ meal, onCopyClick }: HistoryMealCardProps) {
  const { haptic } = useTelegram();
  
  const date = new Date(meal.consumed_at);
  const dayLabel = meal.days_ago === 0 ? 'Сегодня' 
                 : meal.days_ago === 1 ? 'Вчера' 
                 : format(date, 'd MMM', { locale: ru });
  const time = format(date, 'HH:mm');
  const typeRu = mealTypeLabel[meal.meal_type as MealType] ?? meal.meal_type;
  
  function handleCopyClick() {
    haptic('light');
    onCopyClick?.(meal);
  }
  
  return (
    <motion.div
      whileTap={{ scale: 0.99 }}
      className="card p-3"
    >
      <div className="flex items-center gap-2 mb-2">
        <span className="text-[11px] text-emerald-glow font-medium uppercase tracking-wider">
          {dayLabel}
        </span>
        <span className="text-[11px] text-ink-dim">·</span>
        <span className="text-[11px] text-ink-dim">{typeRu}</span>
        <span className="text-[11px] text-ink-dim">·</span>
        <span className="text-[11px] text-ink-dim">{time}</span>
      </div>
      
      <div className="text-sm text-ink mb-2 leading-snug">{meal.preview_text}</div>
      
      <div className="flex items-center justify-between">
        <div className="flex gap-2 text-[11px] text-ink-dim">
          <span>{meal.total_calories} ккал</span>
          <span>·</span>
          <span>{meal.items_count} {plural(meal.items_count, 'продукт', 'продукта', 'продуктов')}</span>
        </div>
        <button
          type="button"
          onClick={handleCopyClick}
          className="text-[11px] font-medium text-emerald-glow bg-emerald-glow/10 hover:bg-emerald-glow/20 px-3 py-1.5 rounded-lg transition-colors active:scale-95 flex items-center gap-1.5"
        >
          <Copy size={11} />
          Копировать
        </button>
      </div>
    </motion.div>
  );
}

// ============================================
// УТИЛИТЫ
// ============================================

function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few;
  return many;
}

function formatLastUsed(iso?: string): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMin < 1) return 'только что';
  if (diffMin < 60) return `${diffMin} мин назад`;
  if (diffHours < 24) return `${diffHours} ч назад`;
  if (diffDays === 0) return 'сегодня';
  if (diffDays === 1) return 'вчера';
  if (diffDays < 7) return `${diffDays} дн назад`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} нед назад`;
  return `${Math.floor(diffDays / 30)} мес назад`;
}
